'use client'

import {
	arrow as floatingArrow,
	autoUpdate,
	flip,
	hide,
	offset,
	shift,
	useFloating,
} from '@floating-ui/react-dom'
import { AnimatePresence, motion } from 'motion/react'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn, isTouchDevice } from '@/utils'

export const Tooltip = ({
	children,
	anchorRef,
	delay = 1000,
	visual = 'default',
	className,
	position = 'bottom',
	hideOnScroll = false,
	forceVisible,
}: {
	children: React.ReactNode
	anchorRef: React.RefObject<HTMLElement | null>
	delay?: number
	visual?: 'default' | 'private' | 'field'
	className?: string
	position?: 'top' | 'top-end' | 'bottom'
	hideOnScroll?: boolean
	forceVisible?: boolean
}) => {
	const [isVisible, setIsVisible] = useState(false)
	const [mounted, setMounted] = useState(false)
	const [suppressed, setSuppressed] = useState(false)
	const isControlled = forceVisible !== undefined

	useEffect(() => {
		setSuppressed(false)
	}, [forceVisible])

	const visible = isControlled ? forceVisible && !suppressed : isVisible
	const isPrivateVisual = visual === 'private'
	const isFieldVisual = visual === 'field'

	const timeoutRef = useRef<number | null>(null)
	const arrowRef = useRef<HTMLDivElement>(null)
	const isTargetedRef = useRef(false)

	const { x, y, refs, placement, middlewareData, update } = useFloating({
		placement: position,
		middleware: [
			offset(
				isFieldVisual
					? { mainAxis: 8, crossAxis: 12 }
					: isPrivateVisual
						? 8
						: 12,
			),
			flip({ fallbackStrategy: 'initialPlacement' }),
			shift({ padding: 8 }),
			floatingArrow({ element: arrowRef }),
			...(hideOnScroll ? [hide({ strategy: 'referenceHidden' })] : []),
		],
		whileElementsMounted: autoUpdate,
	})

	useEffect(() => {
		if (anchorRef.current) {
			refs.setReference(anchorRef.current)
		}
	}, [anchorRef, refs])

	useEffect(() => {
		setMounted(true)
	}, [])

	useEffect(() => {
		const anchor = anchorRef.current
		if (!anchor || isControlled) return

		const showTooltip = () => {
			isTargetedRef.current = true
			if (timeoutRef.current) clearTimeout(timeoutRef.current)
			timeoutRef.current = window.setTimeout(() => {
				if (isTargetedRef.current) {
					setIsVisible(true)
					update()
				}
			}, delay)
		}

		const hideTooltip = () => {
			isTargetedRef.current = false
			if (timeoutRef.current) clearTimeout(timeoutRef.current)
			setIsVisible(false)
		}

		anchor.addEventListener('mouseenter', showTooltip)
		anchor.addEventListener('focus', showTooltip)
		anchor.addEventListener('mouseleave', hideTooltip)
		anchor.addEventListener('blur', hideTooltip)

		if (!isTouchDevice) {
			return () => {
				anchor.removeEventListener('mouseenter', showTooltip)
				anchor.removeEventListener('focus', showTooltip)
				anchor.removeEventListener('mouseleave', hideTooltip)
				anchor.removeEventListener('blur', hideTooltip)
				if (timeoutRef.current) clearTimeout(timeoutRef.current)
			}
		}

		// touchmove cancels any pending show timer regardless of what armed
		// it, since some browsers also fire `focus` on touch-down.
		const TOUCH_MOVE_THRESHOLD = 10
		let touchStart: { x: number; y: number } | null = null
		let touchMoved = false

		const cancelPendingShow = () => {
			isTargetedRef.current = false
			if (timeoutRef.current) clearTimeout(timeoutRef.current)
		}

		const handleTouchStart = (e: TouchEvent) => {
			const touch = e.touches[0]
			touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null
			touchMoved = false
		}

		const handleTouchMove = (e: TouchEvent) => {
			if (!touchStart || touchMoved) return
			const touch = e.touches[0]
			if (!touch) return
			const dx = touch.clientX - touchStart.x
			const dy = touch.clientY - touchStart.y
			if (Math.hypot(dx, dy) > TOUCH_MOVE_THRESHOLD) {
				touchMoved = true
				cancelPendingShow()
			}
		}

		const handleTouchEnd = () => {
			if (!touchMoved) {
				if (isVisible) {
					hideTooltip()
				} else {
					showTooltip()
				}
			}
			touchStart = null
			touchMoved = false
		}

		const handleTouchCancel = () => {
			cancelPendingShow()
			touchStart = null
			touchMoved = false
		}

		anchor.addEventListener('touchstart', handleTouchStart, {
			passive: true,
		})
		anchor.addEventListener('touchmove', handleTouchMove, {
			passive: true,
		})
		anchor.addEventListener('touchend', handleTouchEnd)
		anchor.addEventListener('touchcancel', handleTouchCancel)

		return () => {
			anchor.removeEventListener('mouseenter', showTooltip)
			anchor.removeEventListener('focus', showTooltip)
			anchor.removeEventListener('mouseleave', hideTooltip)
			anchor.removeEventListener('blur', hideTooltip)
			anchor.removeEventListener('touchstart', handleTouchStart)
			anchor.removeEventListener('touchmove', handleTouchMove)
			anchor.removeEventListener('touchend', handleTouchEnd)
			anchor.removeEventListener('touchcancel', handleTouchCancel)
			if (timeoutRef.current) clearTimeout(timeoutRef.current)
		}
	}, [anchorRef, delay, update, isVisible, isControlled])

	useEffect(() => {
		if (!isTouchDevice || !visible) return

		const handleClickOutside = (e: TouchEvent) => {
			const anchor = anchorRef.current
			const tooltip = refs.floating.current
			if (
				anchor &&
				!anchor.contains(e.target as Node) &&
				tooltip &&
				!tooltip.contains(e.target as Node)
			) {
				if (isControlled) {
					setSuppressed(true)
				} else {
					setIsVisible(false)
				}
			}
		}

		document.addEventListener('touchstart', handleClickOutside)
		return () =>
			document.removeEventListener('touchstart', handleClickOutside)
	}, [visible, anchorRef, refs.floating, isControlled])

	useEffect(() => {
		if (!hideOnScroll) return

		const handleScroll = () => {
			if (isControlled) {
				setSuppressed(true)
				return
			}
			isTargetedRef.current = false
			if (timeoutRef.current) clearTimeout(timeoutRef.current)
			setIsVisible(false)
		}

		// Capture phase so scrolls inside nested containers also close it.
		window.addEventListener('scroll', handleScroll, {
			passive: true,
			capture: true,
		})
		return () =>
			window.removeEventListener('scroll', handleScroll, {
				capture: true,
			})
	}, [hideOnScroll, isControlled])

	if (!mounted || !anchorRef.current) return null

	const basePlacement = placement.split('-')[0]
	const staticSide = {
		top: 'bottom',
		right: 'left',
		bottom: 'top',
		left: 'right',
	}[basePlacement!]!

	return createPortal(
		<AnimatePresence>
			{visible && (
				<motion.div
					ref={refs.setFloating}
					initial={{ opacity: 0, scale: 0.95 }}
					animate={{ opacity: 1, scale: 1 }}
					exit={{ opacity: 0, scale: 0.95 }}
					transition={{ duration: 0.15 }}
					style={{
						position: 'absolute',
						top: y ?? 0,
						left: x ?? 0,
						// Must stay mounted (not conditionally rendered):
						// autoUpdate stops recomputing referenceHidden once
						// this element unmounts, so it would never notice
						// the anchor scrolling back into view.
						visibility: middlewareData.hide?.referenceHidden
							? 'hidden'
							: 'visible',
					}}
					className={cn(
						'type-body-caption pointer-events-none z-[60] select-none px-2 py-1',
						isFieldVisual
							? 'w-[260px] rounded-6 bg-white text-dark-grey shadow-[0px_0px_3px_rgba(63,72,103,0.3)]'
							: cn(
									'max-w-[200px] text-white shadow',
									isPrivateVisual
										? 'rounded-4 bg-dark-grey'
										: 'rounded-6 bg-blue',
								),
						className,
					)}
					aria-hidden
					role="presentation"
				>
					{children}
					{!isPrivateVisual && !isFieldVisual && (
						<div
							ref={arrowRef}
							className="absolute size-3 rotate-45 bg-blue"
							style={{
								left: middlewareData.arrow?.x ?? '',
								top: middlewareData.arrow?.y ?? '',
								[staticSide]: '-4px',
							}}
						/>
					)}
				</motion.div>
			)}
		</AnimatePresence>,
		document.body,
	)
}
